import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmt } from '../lib/format';

type MacroEvent = { date: string; impact: string; name: string };

export function KalenderPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['macro-calendar'],
    queryFn: () =>
      api.get<{ count: number; path?: string; events: MacroEvent[] }>('/api/data/macro/calendar?impact=high'),
    refetchInterval: 120_000,
  });

  const events = data?.events || [];
  const now = Date.now();
  const upcoming = events.filter((e) => new Date(e.date).getTime() >= now - 6 * 3600_000);
  const past = events.filter((e) => new Date(e.date).getTime() < now - 6 * 3600_000);

  return (
    <div className="gl-page">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--gl-text)' }}>Kalender makro</div>
        <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 4, maxWidth: 640, lineHeight: 1.5 }}>
          High-impact (CPI, NFP, FOMC, …) untuk <strong>Gate 1</strong>.
          Kalau settle DCD ±6 jam dari event ini, kandidat <strong>dibuang</strong> (bukan rekomendasi).
          Sumber: file <code style={{ color: 'var(--accent-primary)' }}>macro-calendar.json</code>.
        </div>
      </div>

      {isLoading && <div className="gl-empty">Memuat kalender…</div>}
      {error && <div className="settings-alert settings-alert--error">Gagal: {(error as Error).message}</div>}

      {data && (
        <>
          <div className="g-card" style={{ marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Total high-impact</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{data.count}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Mendatang / aktif</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent-primary)' }}>{upcoming.length}</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--gl-text-muted)', alignSelf: 'center' }}>
              Edit file di VPS/repo lalu deploy ulang untuk update tanggal resmi.
            </div>
          </div>

          <div className="g-card" style={{ marginBottom: 16 }}>
            <div className="gl-label">Mendatang (perhatikan Gate 1)</div>
            {upcoming.length === 0 ? (
              <div className="gl-empty">Tidak ada event high-impact ke depan di file.</div>
            ) : (
              <table className="glt">
                <thead>
                  <tr>
                    <th>Waktu (lokal)</th>
                    <th>Event</th>
                    <th>Impact</th>
                    <th>Dalam</th>
                  </tr>
                </thead>
                <tbody>
                  {upcoming.map((e, i) => {
                    const t = new Date(e.date).getTime();
                    const hours = Math.round((t - now) / 3600_000);
                    const near = Math.abs(hours) <= 24;
                    return (
                      <tr key={i}>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmt.dateFull(e.date)}</td>
                        <td style={{ fontWeight: 500, color: 'var(--gl-text)' }}>{e.name}</td>
                        <td><span className="gl-badge gl-badge--danger">{e.impact}</span></td>
                        <td style={{ color: near ? 'var(--warning)' : 'var(--gl-text-muted)' }}>
                          {hours >= 0 ? `${hours}h` : `${Math.abs(hours)}h lalu`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {past.length > 0 && (
            <div className="g-card">
              <div className="gl-label">Sudah lewat (arsip file)</div>
              <table className="glt">
                <thead>
                  <tr>
                    <th>Waktu</th>
                    <th>Event</th>
                  </tr>
                </thead>
                <tbody>
                  {past.slice(-10).reverse().map((e, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--gl-text-faint)' }}>{fmt.dateFull(e.date)}</td>
                      <td style={{ color: 'var(--gl-text-muted)' }}>{e.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
